import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Check, ChevronRight, Loader2, Minus } from "lucide-react"
import { listAllStudentHierarchyItems } from "../../../../api/studentSubjects"
import {
  DEFAULT_LEVEL_LABELS,
  enabledDepth,
  normalizeLabels,
} from "../../../Subjects/detail/hierarchyUtils"
import type { HierarchyItemDto } from "../types"
import "./LearnerTopicTree.style.scss"

interface LearnerTopicTreeProps {
  subjectId: string | null
  levelLabels?: string[]
  selectedIds: string[]
  onChange: (selectedIds: string[]) => void
}

type CheckState = "checked" | "unchecked" | "indeterminate"

const collectKnownDescendants = (
  parentId: string,
  childrenByParent: Record<string, string[]>
): string[] => {
  const direct = childrenByParent[parentId] || []
  const out: string[] = []
  for (const id of direct) {
    out.push(id)
    out.push(...collectKnownDescendants(id, childrenByParent))
  }
  return out
}

const isCoveredBySelection = (
  id: string,
  selected: Set<string>,
  parentById: Record<string, string | null>
): boolean => {
  if (selected.has(id)) return true
  let current = parentById[id] ?? null
  const seen = new Set<string>()
  while (current && !seen.has(current)) {
    if (selected.has(current)) return true
    seen.add(current)
    current = parentById[current] ?? null
  }
  return false
}

interface TreeNodeProps {
  item: HierarchyItemDto
  depth: number
  maxDepth: number
  subjectId: string
  selectedIds: Set<string>
  parentById: Record<string, string | null>
  childrenByParent: Record<string, string[]>
  onToggle: (item: HierarchyItemDto, depth: number) => Promise<void>
  registerChildren: (parentId: string, children: HierarchyItemDto[]) => void
}

const TopicTreeNode = ({
  item,
  depth,
  maxDepth,
  subjectId,
  selectedIds,
  parentById,
  childrenByParent,
  onToggle,
  registerChildren,
}: TreeNodeProps) => {
  const hasChildren = (item.child_count || 0) > 0 && depth < maxDepth

  const checkState: CheckState = useMemo(() => {
    if (isCoveredBySelection(item.id, selectedIds, parentById)) {
      return "checked"
    }
    const known = collectKnownDescendants(item.id, childrenByParent)
    if (known.some((id) => selectedIds.has(id))) return "indeterminate"
    return "unchecked"
  }, [childrenByParent, item.id, parentById, selectedIds])

  const [expanded, setExpanded] = useState(false)
  const [children, setChildren] = useState<HierarchyItemDto[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const loadChildren = useCallback(async () => {
    if (loaded || !hasChildren) return children
    setLoading(true)
    try {
      const items = await listAllStudentHierarchyItems(subjectId, {
        level: depth + 1,
        parent_id: item.id,
      })
      setChildren(items)
      registerChildren(item.id, items)
      setLoaded(true)
      return items
    } catch {
      setChildren([])
      registerChildren(item.id, [])
      setLoaded(true)
      return []
    } finally {
      setLoading(false)
    }
  }, [
    children,
    depth,
    hasChildren,
    item.id,
    loaded,
    registerChildren,
    subjectId,
  ])

  const toggleExpand = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!expanded) await loadChildren()
    setExpanded((v) => !v)
  }

  const handleSelect = () => {
    void onToggle(item, depth)
  }

  const isOn = checkState === "checked"
  const isPartial = checkState === "indeterminate"

  return (
    <div className="learner-topic-tree__node">
      <div
        className={`learner-topic-tree__row${isOn || isPartial ? " is-selected" : ""}`}
        onClick={handleSelect}
        role="checkbox"
        aria-checked={isPartial ? "mixed" : isOn}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleSelect()
          }
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="learner-topic-tree__expand"
            onClick={toggleExpand}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {loading ? (
              <Loader2 size={14} className="is-spinning" />
            ) : (
              <ChevronRight
                size={14}
                className={expanded ? "is-open" : undefined}
              />
            )}
          </button>
        ) : (
          <span className="learner-topic-tree__spacer" aria-hidden />
        )}
        <span className="learner-topic-tree__row-main">
          <span
            className={`learner-topic-tree__check${isOn ? " is-on" : ""}${
              isPartial ? " is-partial" : ""
            }`}
            aria-hidden
          >
            {isOn && <Check size={8} strokeWidth={3} />}
            {isPartial && <Minus size={8} strokeWidth={3} />}
          </span>
          <span className="learner-topic-tree__label">{item.name}</span>
        </span>
      </div>
      {hasChildren && expanded && (
        <div className="learner-topic-tree__children">
          {children.map((child) => (
            <TopicTreeNode
              key={child.id}
              item={child}
              depth={depth + 1}
              maxDepth={maxDepth}
              subjectId={subjectId}
              selectedIds={selectedIds}
              parentById={parentById}
              childrenByParent={childrenByParent}
              onToggle={onToggle}
              registerChildren={registerChildren}
            />
          ))}
          {!loading && loaded && children.length === 0 && (
            <p className="learner-topic-tree__empty">No subtopics</p>
          )}
        </div>
      )}
    </div>
  )
}

const LearnerTopicTree = ({
  subjectId,
  levelLabels,
  selectedIds,
  onChange,
}: LearnerTopicTreeProps) => {
  const labels = normalizeLabels(
    levelLabels && levelLabels.length ? levelLabels : DEFAULT_LEVEL_LABELS
  )
  const maxDepth = enabledDepth(labels)
  const [roots, setRoots] = useState<HierarchyItemDto[]>([])
  const [loading, setLoading] = useState(false)
  const [childrenByParent, setChildrenByParent] = useState<
    Record<string, string[]>
  >({})
  const [parentById, setParentById] = useState<Record<string, string | null>>(
    {}
  )
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  useEffect(() => {
    let cancelled = false
    if (!subjectId || maxDepth < 1) {
      setRoots([])
      setChildrenByParent({})
      setParentById({})
      return
    }
    setLoading(true)
    listAllStudentHierarchyItems(subjectId, { level: 1 })
      .then((items) => {
        if (cancelled) return
        setRoots(items)
        setChildrenByParent({})
        const parents: Record<string, string | null> = {}
        items.forEach((item) => {
          parents[item.id] = item.parent_id
        })
        setParentById(parents)
      })
      .catch(() => {
        if (cancelled) return
        setRoots([])
        setChildrenByParent({})
        setParentById({})
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [subjectId, maxDepth])

  const registerChildren = useCallback(
    (parentId: string, children: HierarchyItemDto[]) => {
      const childIds = children.map((c) => c.id)
      setChildrenByParent((prev) => {
        const existing = prev[parentId]
        if (
          existing &&
          existing.length === childIds.length &&
          existing.every((id, i) => id === childIds[i])
        ) {
          return prev
        }
        return { ...prev, [parentId]: childIds }
      })
      setParentById((prev) => {
        let changed = false
        const next = { ...prev }
        children.forEach((child) => {
          const parent = child.parent_id ?? parentId
          if (next[child.id] !== parent) {
            next[child.id] = parent
            changed = true
          }
        })
        return changed ? next : prev
      })
    },
    []
  )

  const ensureChildren = useCallback(
    async (
      parentId: string,
      parentDepth: number,
      cache: Record<string, string[]>
    ): Promise<string[]> => {
      const cached = cache[parentId]
      if (cached) return cached
      if (!subjectId || parentDepth >= maxDepth) return []
      const items = await listAllStudentHierarchyItems(subjectId, {
        level: parentDepth + 1,
        parent_id: parentId,
      })
      registerChildren(parentId, items)
      const ids = items.map((c) => c.id)
      cache[parentId] = ids
      return ids
    },
    [maxDepth, registerChildren, subjectId]
  )

  const ancestorsOf = useCallback(
    (id: string): string[] => {
      const out: string[] = []
      let current = parentById[id] ?? null
      const seen = new Set<string>()
      while (current && !seen.has(current)) {
        out.push(current)
        seen.add(current)
        current = parentById[current] ?? null
      }
      return out
    },
    [parentById]
  )

  const onToggle = useCallback(
    async (item: HierarchyItemDto, depth: number) => {
      if (!subjectId) return
      const prev = new Set(selectedIds)
      const covered = isCoveredBySelection(item.id, prev, parentById)

      if (!covered) {
        // Select this node only — backend parent id already matches the branch.
        const next = new Set(prev)
        next.add(item.id)
        collectKnownDescendants(item.id, childrenByParent).forEach((id) =>
          next.delete(id)
        )

        let walk: string | null | undefined =
          parentById[item.id] ?? item.parent_id
        while (walk) {
          const siblings = childrenByParent[walk] || []
          if (siblings.length > 0 && siblings.every((id) => next.has(id))) {
            siblings.forEach((id) => next.delete(id))
            next.add(walk)
            walk = parentById[walk]
          } else {
            break
          }
        }

        onChange(Array.from(next))
        return
      }

      // Uncheck: drop this node + known explicit descendants.
      const next = new Set(prev)
      next.delete(item.id)
      collectKnownDescendants(item.id, childrenByParent).forEach((id) =>
        next.delete(id)
      )

      const ancestors = ancestorsOf(item.id)
      const coveringAncestors = ancestors.filter((id) => prev.has(id))
      coveringAncestors.forEach((id) => next.delete(id))

      // Coverage came from an ancestor → keep sibling branches. Only fetch
      // children along the path up to the highest covering ancestor.
      if (coveringAncestors.length > 0) {
        const highestCovering =
          coveringAncestors[coveringAncestors.length - 1]
        const childCache: Record<string, string[]> = { ...childrenByParent }
        let excludeId = item.id
        let childDepth = depth
        for (const ancestorId of ancestors) {
          const parentDepth = childDepth - 1
          if (parentDepth < 1) break
          try {
            const siblingIds = await ensureChildren(
              ancestorId,
              parentDepth,
              childCache
            )
            siblingIds.forEach((id) => {
              if (id !== excludeId) next.add(id)
            })
          } catch {
            /* keep going; branch is still deselected */
          }
          if (ancestorId === highestCovering) break
          excludeId = ancestorId
          childDepth = parentDepth
        }
      }

      onChange(Array.from(next))
    },
    [
      ancestorsOf,
      childrenByParent,
      ensureChildren,
      onChange,
      parentById,
      selectedIds,
      subjectId,
    ]
  )

  if (!subjectId) {
    return (
      <p className="learner-topic-tree__hint">
        Select a subject to browse topics.
      </p>
    )
  }

  if (loading) {
    return (
      <p className="learner-topic-tree__hint">
        <Loader2 size={14} className="is-spinning" /> Loading topics…
      </p>
    )
  }

  if (!roots.length) {
    return <p className="learner-topic-tree__hint">No topics for this subject.</p>
  }

  return (
    <div className="learner-topic-tree" role="group" aria-label="Topic hierarchy">
      {roots.map((item) => (
        <TopicTreeNode
          key={item.id}
          item={item}
          depth={1}
          maxDepth={maxDepth}
          subjectId={subjectId}
          selectedIds={selectedSet}
          parentById={parentById}
          childrenByParent={childrenByParent}
          onToggle={onToggle}
          registerChildren={registerChildren}
        />
      ))}
    </div>
  )
}

export default LearnerTopicTree
